import { useEffect, useRef } from "react";
import Header from "../components/mobile/Header";
import BottomNav from "../components/mobile/BottomNav";
import WaveBackground from "../components/mobile/WaveBackground";
import { usePullRefresh } from "./PullRefreshContext";
import "./MobileLayout.css";

// Pull-to-refresh tuning (px)
const PULL_THRESHOLD = 70;
const PULL_MAX = 110;
const PULL_HOLD = 50;
const PULL_RESISTANCE = 0.5;

// Pull is allowed only when every scrollable element between the touch
// target and the content area is scrolled to the top, and the touch is
// not inside an overlay/modal (position: fixed) or an opted-out element.
const canStartPull = (target, container) => {
    let el = target;

    while (el && el !== container) {
        if (el.nodeType === 1) {
            if (el.closest("[data-no-pull-refresh]")) return false;

            const style = window.getComputedStyle(el);

            if (style.position === "fixed") return false;

            const scrollable =
                /(auto|scroll)/.test(style.overflowY) &&
                el.scrollHeight > el.clientHeight;

            if (scrollable && el.scrollTop > 0) return false;
        }
        el = el.parentNode;
    }

    return container.scrollTop <= 0;
};

// Moves the content and the spinner to the given pull distance.
const renderPull = (pullEl, indicator, value, animate) => {
    if (!pullEl || !indicator) return;

    const transition = animate ? "transform 0.25s ease" : "none";

    pullEl.style.transition = transition;
    indicator.style.transition = `${transition}, opacity 0.25s ease`;

    // Clear transform when idle so position: fixed children
    // (modals) are not trapped inside a transformed ancestor.
    pullEl.style.transform = value > 0 ? `translateY(${value}px)` : "";
    indicator.style.transform = `translateY(${value - PULL_HOLD}px)`;
    indicator.style.opacity = value > 0 ? Math.min(value / PULL_THRESHOLD, 1) : 0;

    const spinner = indicator.firstChild;
    if (spinner) spinner.style.transform = `rotate(${value * 3}deg)`;

    indicator.classList.toggle("ready", value >= PULL_THRESHOLD);
};

export default function MobileLayout({ children, onPunchClick, punchLabel }) {
    const contentRef = useRef(null);
    const pullRef = useRef(null);
    const indicatorRef = useRef(null);

    const { refreshing, refresh } = usePullRefresh();
    const refreshingRef = useRef(refreshing);

    // The page remounts on refresh, so this layout mounts with
    // refreshing = true: hold the spinner, then slide back when done.
    useEffect(() => {
        refreshingRef.current = refreshing;

        if (refreshing) {
            renderPull(pullRef.current, indicatorRef.current, PULL_HOLD, false);
        } else {
            renderPull(pullRef.current, indicatorRef.current, 0, true);
        }
    }, [refreshing]);

    useEffect(() => {
        const container = contentRef.current;
        const pullEl = pullRef.current;
        const indicator = indicatorRef.current;

        if (!container || !pullEl || !indicator) return;

        let startY = 0;
        let startX = 0;
        let distance = 0;
        let tracking = false;
        let pulling = false;

        const onTouchStart = (e) => {
            if (refreshingRef.current || e.touches.length !== 1) return;

            tracking = canStartPull(e.target, container);
            pulling = false;
            distance = 0;
            startY = e.touches[0].clientY;
            startX = e.touches[0].clientX;
        };

        const onTouchMove = (e) => {
            if (!tracking || refreshingRef.current) return;

            const dy = e.touches[0].clientY - startY;
            const dx = e.touches[0].clientX - startX;

            if (!pulling) {
                // Ignore upward and mostly-horizontal swipes
                if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) {
                    if (Math.abs(dy) > 5 || Math.abs(dx) > 5) tracking = false;
                    return;
                }
                if (container.scrollTop > 0) {
                    tracking = false;
                    return;
                }
                pulling = true;
            }

            if (e.cancelable) e.preventDefault();

            distance = Math.min(Math.max(dy, 0) * PULL_RESISTANCE, PULL_MAX);
            renderPull(pullEl, indicator, distance, false);
        };

        const onTouchEnd = () => {
            tracking = false;

            if (!pulling) return;
            pulling = false;

            if (distance < PULL_THRESHOLD) {
                renderPull(pullEl, indicator, 0, true);
                return;
            }

            // Remount routed components so their API calls run again.
            if (!refresh()) {
                renderPull(pullEl, indicator, 0, true);
            }
        };

        container.addEventListener("touchstart", onTouchStart, { passive: true });
        container.addEventListener("touchmove", onTouchMove, { passive: false });
        container.addEventListener("touchend", onTouchEnd, { passive: true });
        container.addEventListener("touchcancel", onTouchEnd, { passive: true });

        return () => {
            container.removeEventListener("touchstart", onTouchStart);
            container.removeEventListener("touchmove", onTouchMove);
            container.removeEventListener("touchend", onTouchEnd);
            container.removeEventListener("touchcancel", onTouchEnd);
        };
    }, [refresh]);

    return (
        <div className="mobile-layout">
            <WaveBackground />
            <div className="mobile-body-layer" aria-hidden="true" />
            <div className="mobile-body-glow" aria-hidden="true" />

            <div className="mobile-header-wrapper">
                <Header />
            </div>

            <main className="mobile-layout-content" ref={contentRef}>
                <div
                    className={`ptr-indicator${refreshing ? " refreshing" : ""}`}
                    ref={indicatorRef}
                    aria-hidden="true"
                >
                    <div className="ptr-spinner" />
                </div>

                <div className="ptr-content" ref={pullRef}>
                    {children}
                </div>
            </main>

            <div className="mobile-footer-wrapper">
                <BottomNav onPunchClick={onPunchClick} punchLabel={punchLabel} />
            </div>

        </div>
    );
}
